import { type ReactNode, useId, useLayoutEffect } from 'react';
import { type IWidgetConfigurationSlot } from './IWidgetConfigurationSlot.ts';
import { useWidgetConfigurationSlotActions } from './WidgetConfigurationSlotContext.tsx';

export interface IWidgetConfigurationSlotRenderProps {
  slot: IWidgetConfigurationSlot;
  /** Ordine dentro la card; i fill senza `order` vanno in coda, in ordine di scoperta. */
  order?: number;
  children: ReactNode;
}

/**
 * "Fill" del sistema slot: non renderizza nulla nel punto di dichiarazione e
 * registra i propri children nello slot indicato; la card corrispondente li
 * mostra tramite `WidgetConfigurationSlotCards`. Allo smontaggio la
 * registrazione viene rimossa, quindi `{condizione && <WidgetConfigurationSlotRender…>}`
 * fa comparire/sparire il contenuto (e la card, se resta vuota).
 *
 * Va dichiarato DOPO le guardie condizionali (`if (!xyz) return null;`), mai
 * con children "vuoti": un fill montato tiene in vita la card del suo slot.
 */
export function WidgetConfigurationSlotRender(
  props: IWidgetConfigurationSlotRenderProps,
) {
  const { slot, order, children } = props;
  const { upsert, unregister } = useWidgetConfigurationSlotActions();
  const key = useId();

  // Upsert a OGNI render, senza array di deps: `children` cattura props e
  // stato correnti del produttore e deve restare fresco nella card.
  // L'upsert è idempotente per `key` e in layout effect, così le card sono già
  // aggiornate prima del paint (niente flash al primo render del drawer).
  useLayoutEffect(() => {
    upsert({ key, slot, order, node: children });
  });

  // Cleanup separato con deps stabili: rimuove l'entry solo allo smontaggio.
  useLayoutEffect(() => {
    return () => unregister(key);
  }, [unregister, key]);

  return null;
}
